import { apiMerchantPublic } from "@/api"
import { record, text, oneOf } from "@/lib/scheduling/wire"
import type { DeliveryAreaCity, DeliveryAreaConfig, DeliveryAreaNeighborhood } from "./types"

const CENTS_PER_REAL = 100

function centsToReais(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value / CENTS_PER_REAL : 0
}

function minutes(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

function parseNeighborhood(raw: unknown): DeliveryAreaNeighborhood | null {
  const source = record(raw)
  if (!source) return null
  const id = text(source.id)
  const name = text(source.name)
  if (!id || !name) return null
  return {
    id,
    name,
    deliveryFeeReais: centsToReais(source.deliveryFeeCents),
    deliveryTimeMinutes: minutes(source.deliveryTime),
  }
}

function parseNeighborhoods(raw: unknown): DeliveryAreaNeighborhood[] {
  if (!Array.isArray(raw)) return []
  return raw
    .map(parseNeighborhood)
    .filter((neighborhood): neighborhood is DeliveryAreaNeighborhood => neighborhood !== null)
}

function parseCity(raw: unknown): DeliveryAreaCity | null {
  const source = record(raw)
  if (!source) return null
  const id = text(source.id)
  const name = text(source.name)
  const state = text(source.state)
  if (!id || !name || !state) return null
  return { id, name, state, neighborhoods: parseNeighborhoods(source.neighborhoods) }
}

function parseCities(raw: unknown): DeliveryAreaCity[] {
  if (!Array.isArray(raw)) return []
  return raw.map(parseCity).filter((city): city is DeliveryAreaCity => city !== null)
}

/**
 * Narrows the untyped delivery-area payload. An unrecognised `serviceAreaMode`
 * falls back to `POLYGON` — the mode that changes nothing for a caller who
 * still evaluates the catalog's drawn polygon.
 */
export function parseDeliveryAreaConfig(raw: unknown): DeliveryAreaConfig | null {
  const source = record(raw)
  if (!source) return null
  const mode = oneOf(source.serviceAreaMode, ["POLYGON", "NEIGHBORHOOD"], "POLYGON")
  if (mode === "POLYGON") return { mode: "POLYGON" }
  return {
    mode: "NEIGHBORHOOD",
    cities: parseCities(source.cities),
    neighborhoods: parseNeighborhoods(source.neighborhoods),
  }
}

/**
 * Reads how a store delivers: POLYGON (unchanged catalog geofencing) or
 * NEIGHBORHOOD (served cities/neighborhoods, fee and ETA per neighborhood).
 * Server-cached, so polling it is cheap. Resolves `null` on any failure —
 * callers must never guess NEIGHBORHOOD coverage from a failed read.
 */
export async function fetchDeliveryAreaConfig(
  merchantId: string,
  signal?: AbortSignal,
): Promise<DeliveryAreaConfig | null> {
  const { data } = await apiMerchantPublic.get<unknown>(
    `/public/merchants/${encodeURIComponent(merchantId)}/delivery-area`,
    { signal },
  )
  return parseDeliveryAreaConfig(data)
}
